import { ButtonColor } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { COMPACT_CHECK } from "components/Inputs/compact";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { PersonLinkPill } from "./ActivityTabParts";
import { useAutomationsApi } from "./hooks";
import type {
  TurnOn,
  TurnOnPeople,
  TurnOnPreview,
  TurnOnTarget,
} from "./types";
import { plural } from "./words";

/** Who it reaches, as one sentence, around its number. */
function reachWords(preview: TurnOnPreview, number: ReactNode) {
  if (preview.schedule)
    return preview.people.count ? (
      <>Its next run would reach {number}.</>
    ) : (
      "Its next run would reach nobody."
    );
  return preview.people.count ? (
    <>In the next day, it would reach {number}.</>
  ) : (
    "In the next day, it would reach nobody new."
  );
}

/** How many people, as a button that shows or hides their names. */
function PeopleNumber({
  people,
  open,
  onToggle,
}: {
  people: TurnOnPeople;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className="font-semibold text-green-700 hover:underline"
      aria-expanded={open}
      onClick={onToggle}
    >
      {plural(people.count, "person", "people")}
    </button>
  );
}

/**
 * Their names as pills that open their pages. The pill's color is their
 * kind, as on Activity; hovering says it too when the list mixes kinds.
 */
function PeopleList({ people }: { people: TurnOnPeople }) {
  const kindOf = (key: string) => key.split(":")[0];
  const mixed = new Set(people.named.map((p) => kindOf(p.key))).size > 1;
  const more = people.count - people.named.length;
  return (
    <ul className="mt-2 flex max-h-40 flex-wrap content-start items-center gap-1 overflow-y-auto rounded border border-gray-200 p-2">
      {people.named.map(({ key, name }) => {
        const label = name || "No name";
        return (
          <li key={key} className="min-w-0 max-w-full">
            <PersonLinkPill
              person={key}
              label={label}
              title={mixed ? `${label}, ${kindOf(key)}` : label}
            />
          </li>
        );
      })}
      {more > 0 && (
        <li className="px-1 text-xs text-gray-500">and {more} more</li>
      )}
    </ul>
  );
}

/**
 * Before an automation turns on: who it reaches now, and, when some
 * already match what it watches for, a box (unticked) to do it for them
 * now too. Each number shows its people's names on a click. Turning it on
 * carries the counts the backend compares (seen), so it refuses if they
 * changed since; then it counts again.
 */
export default function TurnOnDialog({
  name,
  target,
  onTurnOn,
  onClose,
}: {
  name: string;
  /** A saved automation (its id), or a draft to save on. */
  target: TurnOnTarget;
  /** Turns it on as shown. */
  onTurnOn: (turnOn: TurnOn) => Promise<unknown>;
  onClose: () => void;
}) {
  const api = useAutomationsApi();
  const [preview, setPreview] = useState<TurnOnPreview>();
  const [error, setError] = useState<string>();
  // Why turning it on was refused: it stays while it counts again.
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [includeReady, setIncludeReady] = useState(false);
  const [showReached, setShowReached] = useState(false);
  const [showReady, setShowReady] = useState(false);
  const targetRef = useRef(target);

  const count = useCallback(() => {
    setPreview(undefined);
    setError(undefined);
    setIncludeReady(false);
    api
      .previewTurnOn(targetRef.current)
      .then(setPreview)
      .catch((e) =>
        setError(errorText(e, "Who it reaches could not be counted."))
      );
  }, [api]);

  useEffect(count, [count]);

  const turnOn = () => {
    if (!preview) return;
    setProblem(undefined);
    setBusy(true);
    onTurnOn({
      seen: preview.seen,
      includeReady: includeReady && preview.ready.count > 0,
    })
      .catch((e) => {
        const reason = errorText(e, "It could not be turned on.");
        setProblem(reason);
        // Who it reaches changed: count again before trying once more.
        if (reason.includes("changed")) count();
      })
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="dialog-content">
        <h2 className="mb-2 text-xl font-semibold">Turn on {name}?</h2>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!preview && !error && (
          <div className="h-5 animate-pulse rounded bg-gray-200" />
        )}
        {preview && (
          <p className="text-sm text-gray-700">
            {reachWords(
              preview,
              <PeopleNumber
                people={preview.people}
                open={showReached}
                onToggle={() => setShowReached((shown) => !shown)}
              />
            )}
          </p>
        )}
        {preview && showReached && preview.people.count > 0 && (
          <PeopleList people={preview.people} />
        )}
        {preview && preview.ready.count > 0 && (
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-900">
            <input
              type="checkbox"
              className={COMPACT_CHECK}
              checked={includeReady}
              onChange={(e) => setIncludeReady(e.target.checked)}
            />
            <span>
              Also do it now for the{" "}
              <PeopleNumber
                people={preview.ready}
                open={showReady}
                onToggle={() => setShowReady((shown) => !shown)}
              />{" "}
              who already {preview.ready.count === 1 ? "matches" : "match"}
            </span>
          </label>
        )}
        {preview && showReady && preview.ready.count > 0 && (
          <PeopleList people={preview.ready} />
        )}
        {problem && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {problem}
          </p>
        )}
        <DialogActions>
          <DialogAction
            primary={true}
            color={ButtonColor.GREEN}
            disabled={!preview || busy}
            onClick={turnOn}
          >
            Turn it on
          </DialogAction>
          <DialogClose asChild>
            <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
          </DialogClose>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
